import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";

const buildNestedErrors = (
  errors: Yup.ValidationError[],
): Record<string, unknown> => {
  return errors.reduce(
    (acc, error) => {
      if (!error.path) return acc;

      const pathArray = error.path.split(".");
      pathArray.reduce((a, b, index) => {
        if (index + 1 === pathArray.length) {
          a[b] = error.message;
          return null;
        } else {
          if (!a[b]) a[b] = {};
          return a[b];
        }
      }, acc);

      return acc;
    },
    {} as Record<string, unknown>,
  );
};

const onValidationSchema = async (
  shape: Record<string, Yup.AnySchema>,
  data: unknown,
) => {
  const response = {
    isValid: false,
    errors: {},
  };

  const schema = Yup.object().shape(shape);

  await schema
    .validate(data, { abortEarly: false })
    .then(() => {
      response.isValid = true;
      response.errors = {};
    })
    .catch((err) => {
      if (err instanceof Yup.ValidationError) {
        response.isValid = false;
        response.errors = buildNestedErrors(err.inner);
      }
    });

  return response;
};

export const useValidationErrors = (
  shape: Record<string, Yup.AnySchema>,
  data: unknown,
) => {
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [isValid, setIsValid] = useState<boolean>(false);
  const [listenErrors, setListenErrors] = useState<boolean>(false);

  const memoizedData = useMemo(() => data, [JSON.stringify(data)]);
  const memoizedShape = useMemo(() => shape, [JSON.stringify(shape)]);

  const previousErrors = useRef(errors);
  const previousIsValid = useRef(isValid);

  useEffect(() => {
    let isMounted = true;

    const validate = async () => {
      const response = await onValidationSchema(memoizedShape, memoizedData);

      if (isMounted) {
        if (response.isValid !== previousIsValid.current) {
          setIsValid(response.isValid);
          previousIsValid.current = response.isValid;
        }

        if (
          listenErrors &&
          JSON.stringify(response.errors) !==
            JSON.stringify(previousErrors.current)
        ) {
          setErrors(response.errors);
          previousErrors.current = response.errors;
        }
      }
    };

    validate();

    return () => {
      isMounted = false;
    };
  }, [listenErrors, memoizedShape, memoizedData]);

  const onHandleListenErrors = useCallback((listen: boolean) => {
    setListenErrors(listen);

    if (!listen) {
      setErrors({});
    }
  }, []);

  return { isValid, errors, onHandleListenErrors };
};
